import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Avatar, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { BoltOutlined as BoltOutlinedIcon } from "../icons/FigmaControlIcons";
import RichTextField from "../RichTextField";
import { fetchTeamRoster } from "../../utils/teamMembers";
import { communicationRequest } from "../../utils/contactCommunication";
import { narrativeEditorHtml } from "../../utils/narrativeHtml";
import { toolbarPillButtonSx } from "../../utils/pillButtonStyles";

export default function ContentEditorFields({ draft, onChange, disabled = false, sources = [] }) {
  const [owners, setOwners] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(true);
  const [ownerError, setOwnerError] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState("");
  const [suggestion, setSuggestion] = useState("");
  useEffect(() => {
    let active = true;
    fetchTeamRoster({ apiBase: process.env.REACT_APP_API_BASE || "" })
      .then((rows) => { if (active) setOwners(rows); })
      .catch(() => { if (active) setOwnerError("Unable to load team members. You can still enter an owner."); })
      .finally(() => { if (active) setOwnerLoading(false); });
    return () => { active = false; };
  }, []);
  const update = (name, value) => onChange({ ...draft, [name]: value });
  const field = (name, label, props = {}) => <TextField fullWidth disabled={disabled} label={label} value={draft[name] || ""} onChange={(event) => update(name, event.target.value)} {...props} />;
  const generate = async () => {
    setGenerating(true); setAiError("");
    try {
      const result = await communicationRequest("ai-agent/content-draft", { instructions, content_type: draft.content_type_key, title: draft.title, content: draft.description });
      setSuggestion(narrativeEditorHtml(result.html));
    } catch (err) { setAiError(err.message || "Unable to generate content."); }
    finally { setGenerating(false); }
  };
  return <>
    {field("title", "Title", { required: true, inputProps: { maxLength: 255 } })}
    <Box>
      <Button className="pill" sx={{ ...toolbarPillButtonSx, mb: 1.5 }} startIcon={<BoltOutlinedIcon />} disabled={disabled || generating} onClick={() => setAiOpen((value) => !value)}>AI Agent</Button>
      {aiOpen && <Stack spacing={1.5} sx={{ mb: 2, p: 2, border: "1px solid #EAECF0", borderRadius: 2 }}>
        <TextField label="What should the content say?" multiline minRows={2} value={instructions} disabled={disabled || generating} onChange={(event) => setInstructions(event.target.value)} inputProps={{ maxLength: 4000 }} />
        {aiError && <Alert severity="error">{aiError}</Alert>}
        <Button disabled={disabled || generating || !instructions.trim()} onClick={generate}>{generating ? "Generating..." : "Generate Draft"}</Button>
        {suggestion && <>
          <Box sx={{ overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: suggestion }} />
          <Button disabled={disabled || generating} onClick={() => { update("description", suggestion); setSuggestion(""); setAiOpen(false); }}>Use Draft</Button>
        </>}
      </Stack>}
      <Typography sx={{ color: "#3451F4", fontSize: 13, fontWeight: 600 }}>Content</Typography>
      <RichTextField label="Content" value={draft.description || ""} disabled={disabled} rows={8} onChange={(html) => update("description", html)} />
    </Box>
    <Autocomplete freeSolo options={owners} loading={ownerLoading} disabled={disabled}
      value={draft.owner_name || ""} inputValue={draft.owner_name || ""}
      getOptionLabel={(option) => typeof option === "string" ? option : option.name}
      onInputChange={(_event, value, reason) => { if (reason === "input" || reason === "clear") update("owner_name", value); }}
      onChange={(_event, value) => update("owner_name", typeof value === "string" ? value : value?.name || "")}
      renderOption={(props, option) => <Box component="li" {...props} key={option.id || option.name} sx={{ gap: 1 }}><Avatar src={option.avatar_url} sx={{ width: 28, height: 28, fontSize: 12 }}>{option.name.slice(0, 1)}</Avatar>{option.name}</Box>}
      renderInput={(params) => <TextField {...params} label="Owner" helperText={ownerError || "Search team members"} inputProps={{ ...params.inputProps, maxLength: 255 }} />} />
    {field("related_entity_label", "Related Entity", { inputProps: { maxLength: 100 } })}
    <Autocomplete freeSolo options={[...new Set([...sources, draft.source].filter(Boolean))].sort()} disabled={disabled}
      value={draft.source || ""} inputValue={draft.source || ""}
      onInputChange={(_event, value, reason) => { if (reason === "input" || reason === "clear") update("source", value); }}
      onChange={(_event, value) => update("source", value || "")}
      renderInput={(params) => <TextField {...params} label="Source" helperText="Search existing sources or enter a new source" inputProps={{ ...params.inputProps, maxLength: 100 }} />} />
    {field("tags", "Tags", { helperText: "Separate tags with commas." })}
  </>;
}
